import { useEffect, useRef, useCallback, useState } from 'react';
import LoadingSpinner from '../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import styles from './CardList.module.scss';

// Generic mobile card list: page accumulation, last-card-observer infinite
// scroll, shimmer and empty states. Used by both the Leads and Deals tabs —
// the redux stores replace data per page, so the list accumulates locally.
type CardListProps<T> = {
    items: T[];
    loading: boolean;
    getId: (item: T) => string | undefined;
    renderCard: (item: T) => React.ReactNode;
    emptyState: React.ReactNode;
    hasNextPage?: boolean;
    onLoadMore?: () => void;
    // True while the current items belong to page 1 — the list replaces
    // instead of appending (leads pass currentPageNumber <= 1)
    replaceAll?: boolean;
    // Changing this value clears the accumulation (category / view switches)
    resetKey?: unknown;
    dataAttrs?: (item: T) => Record<string, string | undefined>;
};

const SKELETON_CARDS = 4;

const SkeletonCard = () => (
    <div className={styles.Skeleton}>
        <div className={styles.SkeletonHeader}>
            <div className={styles.SkeletonAvatar} />
            <div className={styles.SkeletonLines}>
                <div className={styles.SkeletonLine} style={{ width: '60%' }} />
                <div className={styles.SkeletonLine} style={{ width: '40%' }} />
            </div>
        </div>
        <div className={styles.SkeletonBody}>
            <div className={styles.SkeletonLine} style={{ width: '90%' }} />
            <div className={styles.SkeletonLine} style={{ width: '70%' }} />
        </div>
    </div>
);

const CardList = <T,>({
    items,
    loading,
    getId,
    renderCard,
    emptyState,
    hasNextPage = false,
    onLoadMore,
    replaceAll = false,
    resetKey,
    dataAttrs,
}: CardListProps<T>) => {
    const observerRef = useRef<IntersectionObserver | null>(null);
    const isLoadingMore = useRef<boolean>(false);
    const hasFetchedOnce = useRef<boolean>(false);
    const prevItemsRef = useRef<T[]>([]);
    const lastResetKeyRef = useRef<unknown>(resetKey);
    const [accumulated, setAccumulated] = useState<T[]>([]);

    // Clear accumulation when the caller switches context (category / view)
    useEffect(() => {
        if (lastResetKeyRef.current !== resetKey) {
            lastResetKeyRef.current = resetKey;
            setAccumulated([]);
        }
    }, [resetKey]);

    // getId is a render-created callback from the parent; keep it in a ref so the
    // accumulate effect below can use it without listing it as a dep (which would
    // re-run the effect on every parent render)
    const getIdRef = useRef(getId);
    useEffect(() => {
        getIdRef.current = getId;
    });

    useEffect(() => {
        if (loading) hasFetchedOnce.current = true;
        // A load cycle ended (success or failure) — allow the next load-more
        if (!loading) isLoadingMore.current = false;
        if (items === prevItemsRef.current) return;
        prevItemsRef.current = items;

        if (items.length === 0) {
            if (!loading) setAccumulated([]);
            return;
        }

        if (replaceAll) {
            setAccumulated(items);
        } else {
            setAccumulated((prev) => {
                if (prev.length === 0) return [...items];
                const getIdFn = getIdRef.current;
                const existingIds = new Set(prev.map(getIdFn));
                const fresh = items.filter((item) => getIdFn(item) && !existingIds.has(getIdFn(item)));
                if (fresh.length === 0) return prev;
                return [...prev, ...fresh];
            });
        }
        isLoadingMore.current = false;
    }, [items, loading, replaceAll]);

    const loadNextPage = useCallback(() => {
        if (!hasNextPage || loading || isLoadingMore.current || !onLoadMore) return;
        isLoadingMore.current = true;
        onLoadMore();
    }, [hasNextPage, loading, onLoadMore]);

    // Observe the last card via callback ref — an effect keyed on props misses
    // the node: accumulated items render one commit after data arrives
    const lastCardRef = useCallback(
        (node: HTMLDivElement | null) => {
            if (loading) return;
            if (observerRef.current) observerRef.current.disconnect();
            if (!hasNextPage) return;

            observerRef.current = new IntersectionObserver(
                ([entry]) => {
                    if (entry?.isIntersecting) loadNextPage();
                },
                { rootMargin: '300px', threshold: 0 },
            );
            if (node) observerRef.current.observe(node);
        },
        [loading, hasNextPage, loadNextPage],
    );

    // Shimmer until the first load completes, and across the one-commit gap
    // where items have arrived but accumulation hasn't run yet — falling into
    // the empty state there flashes it before the cards paint
    if (accumulated.length === 0 && (loading || !hasFetchedOnce.current || items.length > 0)) {
        return (
            <div className={styles.List}>
                {Array.from({ length: SKELETON_CARDS }).map((_, i) => (
                    <SkeletonCard key={i} />
                ))}
            </div>
        );
    }

    if (!loading && accumulated.length === 0) {
        return <div className={styles.EmptyState}>{emptyState}</div>;
    }

    return (
        <div className={styles.List}>
            {accumulated.map((item, idx) => (
                <div
                    key={getId(item) || `card-${idx}`}
                    ref={idx === accumulated.length - 1 ? lastCardRef : undefined}
                    {...(dataAttrs ? dataAttrs(item) : {})}
                >
                    {renderCard(item)}
                </div>
            ))}
            {(hasNextPage || loading) && (
                <div className={styles.LoadMore}>{loading && <LoadingSpinner height="1.5rem" color="var(--primary-purple)" />}</div>
            )}
        </div>
    );
};

export default CardList;
